/**
 * api/endpoints.ts
 * ──────────────────────────────────────────────────────────────────────────
 * SINGLE SOURCE OF TRUTH for every backend URL used by SK Company Cash.
 *
 * Two backend services:
 *   PLATFORM_API      → skplatform-api      identity only (/me, refresh, logout)
 *   COMPANY_CASH_API  → sk-company-cash-api the product API
 *
 * This console owns NO authentication UI. Sign-in, sign-up, password recovery
 * and MFA all live in skplatform-fe; the only platform endpoints here are the
 * three a signed-in session needs: probe, refresh, sign out.
 *
 * Base URLs are injected at BUILD time by webpack DefinePlugin
 * (web/webpack.config.js) from the environment, so a stage and a prod bundle
 * are produced from the identical commit. On native the same names are read
 * from the Metro-time environment.
 *
 * RULES
 *   - Zero hardcoded URLs anywhere else in the codebase.
 *   - To move an environment, change ONLY the three base URLs below.
 *   - Every path here is verified 1:1 against
 *     sk-company-cash-api/src/routes/v1/*.ts.
 * ──────────────────────────────────────────────────────────────────────────
 */

/** skplatform-api — issues the httpOnly session cookies. Local dev: :3013. */
export const PLATFORM_API = process.env.PLATFORM_API_URL || 'http://localhost:3013';

/** sk-company-cash-api — assignments, ledger, approvals, config. Local: :3015. */
export const COMPANY_CASH_API = process.env.COMPANY_CASH_API_URL || 'http://localhost:3015';

/** skplatform-fe — where a visitor with no session is sent to sign in. */
export const PLATFORM_APP_ORIGIN = process.env.APP_ORIGIN_PLATFORM || 'http://localhost:3009';

/** Versioned prefixes. */
const P = `${PLATFORM_API}/v1`;
const C = `${COMPANY_CASH_API}/v1`;

/* ═══════════════════════════════════════════════════════════════════════════
   PLATFORM API — session only (no auth UI lives in this product)
═══════════════════════════════════════════════════════════════════════════ */

/** GET — identity probe. 200 = signed in, 401 = not. */
export const Platform_Me_Url = `${P}/me`;
/** POST — silent access-token rotation from the refresh cookie. */
export const Platform_Refresh_Token_Url = `${P}/auth/refresh`;
/** POST — clears the session cookies (204). */
export const Platform_Logout_Url = `${P}/auth/logout`;

/*
 * The sign-in URL is NOT built here. It needs the product slug, a `state` nonce
 * and a matching `sk_auth_state` cookie, all of which must be produced together
 * — so it lives in navigation/platformAuthRedirect.ts, matching the other SK
 * products. A `return_to`-only URL is accepted by the platform and then
 * ignored, which strands the user on its dashboard after signing in.
 */

/* ═══════════════════════════════════════════════════════════════════════════
   COMPANY CASH API — /v1/config   (Settings + the shell's boot call)
═══════════════════════════════════════════════════════════════════════════ */

/** GET — console role, gate map, policy, rules, options, categories. One call. */
export const Cash_Bootstrap_Url = `${C}/config/bootstrap`;

export const Cash_Options_Url = `${C}/config/options`;
export const Cash_Option_Url = (id: string) => `${C}/config/options/${encodeURIComponent(id)}`;

export const Cash_Categories_Url = `${C}/config/categories`;
export const Cash_Category_Url = (id: string) => `${C}/config/categories/${encodeURIComponent(id)}`;

/** GET / PATCH — base currency, thresholds, variance routing, connectors. */
export const Cash_Policy_Url = `${C}/config/policy`;

/** GET / PATCH — staleDays, receiptRequired, escalateHours. */
export const Cash_Rules_Url = `${C}/config/rules`;

export const Cash_Approval_Rules_Url = `${C}/config/approval-rules`;
export const Cash_Approval_Rule_Url = (id: string) =>
  `${C}/config/approval-rules/${encodeURIComponent(id)}`;

/** GET — configuration change audit trail. */
export const Cash_Config_Audit_Url = `${C}/config/audit`;

/* ═══════════════════════════════════════════════════════════════════════════
   COMPANY CASH API — /v1/cash   (register, ledger drawer, custodian console)
═══════════════════════════════════════════════════════════════════════════ */

/** GET (list, filterable) / POST (assign cash). */
export const Cash_Assignments_Url = `${C}/cash/assignments`;

/** GET — the drawer's single round trip: assignment + ledger + transfers. */
export const Cash_Assignment_Url = (id: string) =>
  `${C}/cash/assignments/${encodeURIComponent(id)}`;

/** GET (by assignment) / POST (record spend, collection, deposit, replenish). */
export const Cash_Transactions_Url = `${C}/cash/transactions`;

/** POST — append a reversing row. There is no PATCH or DELETE, by design. */
export const Cash_Transaction_Reverse_Url = (id: string) =>
  `${C}/cash/transactions/${encodeURIComponent(id)}/reverse`;

export const Cash_Assignment_Transfer_Url = (id: string) =>
  `${C}/cash/assignments/${encodeURIComponent(id)}/transfer`;

export const Cash_Assignment_Close_Purpose_Url = (id: string) =>
  `${C}/cash/assignments/${encodeURIComponent(id)}/close-purpose`;

/** POST — mint a presigned evidence upload; the browser never calls Drive. */
export const Cash_Assignment_Evidence_Url = (id: string) =>
  `${C}/cash/assignments/${encodeURIComponent(id)}/evidence`;

/** Custodian console — scoped by the caller's own token, never by a query param. */
export const Cash_My_Assignments_Url = `${C}/cash/my/assignments`;
export const Cash_My_Transactions_Url = `${C}/cash/my/transactions`;

/* ═══════════════════════════════════════════════════════════════════════════
   COMPANY CASH API — /v1/approvals
═══════════════════════════════════════════════════════════════════════════ */

/** GET — the queue, filterable by state. */
export const Cash_Approvals_Url = `${C}/approvals`;
/** GET — the three approver KPI cards, totals grouped per currency. */
export const Cash_Approvals_Summary_Url = `${C}/approvals/queue-summary`;
/** GET — the read-only policy quad on "Policy in force". */
export const Cash_Approvals_Policy_Url = `${C}/approvals/policy-in-force`;
/** POST — approve or reject one item. A rejection must carry a reason. */
export const Cash_Approval_Decision_Url = (id: string) =>
  `${C}/approvals/${encodeURIComponent(id)}/decision`;
/** POST — "Approve all" on the approver queue. */
export const Cash_Approvals_Bulk_Url = `${C}/approvals/bulk-decision`;

/* ═══════════════════════════════════════════════════════════════════════════
   COMPANY CASH API — /v1/reconciliation, /v1/sync, /v1/settlement, /v1/overview
═══════════════════════════════════════════════════════════════════════════ */

/** GET — one card per count. Gated on variance routing. */
export const Cash_Reconciliations_Url = `${C}/reconciliation`;
/** POST — record a cash count. */
export const Cash_Counts_Url = `${C}/reconciliation/counts`;
/** POST — waive or route a variance. */
export const Cash_Reconciliation_Resolve_Url = (id: string) =>
  `${C}/reconciliation/${encodeURIComponent(id)}/resolve`;

/** GET — the outbox. Gated on Payroll/Billing being connected. */
export const Cash_Sync_Url = `${C}/sync`;
/** POST — re-send a failed event under the same identity (idempotent). */
export const Cash_Sync_Retry_Url = (id: string) => `${C}/sync/${encodeURIComponent(id)}/retry`;

/** GET — leaving custodians still holding cash. Gated on the offboarding feed. */
export const Cash_Settlements_Url = `${C}/settlement`;
/** POST — settle one leaver. */
export const Cash_Settlement_Resolve_Url = (id: string) =>
  `${C}/settlement/${encodeURIComponent(id)}/resolve`;

/** GET — KPIs, charts and the needs-attention list for the Finance Overview. */
export const Cash_Overview_Url = `${C}/overview`;
